// Geometria da etiqueta de arte estendida em pontos PDF. Entrada em mm
// (GET /admin/art-labels/geometry; fallback DEFAULT_GEOMETRY). Origem: topo-esquerdo, y desce.
import { PAGE } from "./constants";
import { DEFAULT_GEOMETRY } from "../../../artLabels/artLabelPreviewMath";

export const PT_PER_MM = 72 / 25.4;
export const ART_PER_PAGE = 8;
const COLS = 2;
const ROWS = 4;

/**
 * Placas em mm, relativas à caixa de corte da frente (66×30, janela do slab CGC).
 * Tudo dentro da área segura (`safeInset` 3 mm → x 3..63, y 3..27): a `meta` fecha
 * em 20,5 + 5 = 25,5 mm. Se o G1 mudar `labelWidth`/`frontHeight`, recalibrar aqui.
 */
export const PLATE_MM = {
  name: { x: 3, y: 3, w: 40, h: 8 },
  grade: { x: 45, y: 3, w: 18, h: 12 },
  subgrades: { x: 3, y: 12.5, w: 40, h: 5.5 },
  meta: { x: 3, y: 20.5, w: 60, h: 5 },
};

export const mmToPt = (mm) => mm * PT_PER_MM;
const rect = (x, y, width, height) => ({ x, y, width, height });
const withDefaults = (geometry) => ({ ...DEFAULT_GEOMETRY, ...(geometry || {}) });

export function artUnitPt(geometry) {
  const g = withDefaults(geometry);
  const width = mmToPt(g.labelWidth + 2 * g.bleed);
  const frontH = mmToPt(g.frontHeight + 2 * g.bleed);
  const backH = mmToPt(g.backHeight + 2 * g.bleed);
  const bleedPt = mmToPt(g.bleed);
  return {
    unit: { width, height: frontH + backH },
    front: rect(0, 0, width, frontH),
    back: rect(0, frontH, width, backH),
    frontTrim: rect(bleedPt, bleedPt, mmToPt(g.labelWidth), mmToPt(g.frontHeight)),
    backTrim: rect(bleedPt, frontH + bleedPt, mmToPt(g.labelWidth), mmToPt(g.backHeight)),
    bleedPt,
    fold: frontH,
  };
}

/**
 * Sistema de coordenadas da frente do papel "art" na tela: o corte real
 * (labelWidth × frontHeight) em pt, origem no topo-esquerdo do corte. Mesma fonte
 * do PDF (artUnitPt.frontTrim), então tela e impresso escalam igual nos dois eixos.
 */
export function artFacePt(geometry) {
  const g = withDefaults(geometry);
  const { frontTrim, backTrim, bleedPt } = artUnitPt(g);
  return { width: frontTrim.width, height: frontTrim.height, backHeight: backTrim.height, bleed: bleedPt };
}

export function artSlotsPt(geometry) {
  const { unit } = artUnitPt(geometry);
  const gapX = (PAGE.width - COLS * unit.width) / (COLS + 1);
  const gapY = (PAGE.height - ROWS * unit.height) / (ROWS + 1);
  const slots = [];
  for (let row = 0; row < ROWS; row += 1) {
    for (let col = 0; col < COLS; col += 1) {
      slots.push(rect(gapX + col * (unit.width + gapX), gapY + row * (unit.height + gapY), unit.width, unit.height));
    }
  }
  return slots;
}

export function artSlotForIndex(indexOnPage, geometry) {
  if (indexOnPage < 0 || indexOnPage >= ART_PER_PAGE) throw new Error(`slot art invalido: ${indexOnPage}`);
  return artSlotsPt(geometry)[indexOnPage];
}

/** Placas em pt relativas ao topo-esquerdo da unidade (sangria incluída). */
export function platesPt(geometry) {
  const g = withDefaults(geometry);
  const out = {};
  for (const [key, p] of Object.entries(PLATE_MM)) {
    out[key] = rect(mmToPt(g.bleed + p.x), mmToPt(g.bleed + p.y), mmToPt(p.w), mmToPt(p.h));
  }
  return out;
}

/** Regiões das placas em px do PNG aprovado (largura do PNG = labelWidth + 2·bleed mm). */
export function plateRegionsPx(geometry, pngWidth) {
  const g = withDefaults(geometry);
  const pxPerMm = pngWidth / (g.labelWidth + 2 * g.bleed);
  const out = {};
  for (const [key, p] of Object.entries(PLATE_MM)) {
    out[key] = rect(Math.round((g.bleed + p.x) * pxPerMm), Math.round((g.bleed + p.y) * pxPerMm), Math.round(p.w * pxPerMm), Math.round(p.h * pxPerMm));
  }
  return out;
}
